import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { apiClient, offersAPI, revisionsAPI } from '../utils/api';
import { Offerta, Template } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import OfferSections from './OfferSections';
import OfferRevisions from './OfferRevisions';

type Generated = { revision_id: string; numero_revisione: number; bozza: boolean; documents: { id: string; tipo: string; url: string; versione: number }[] };
function message(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Operazione non riuscita. Riprova.';
}

export default function OfferDocumentPanel({ offer, templates, blocked, onDirtyChange }: {
  offer: Offerta; templates: Template[]; blocked: boolean; onDirtyChange: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = offer.stato === 'bozza' && hasPermission('offerte:edit');
  const canExport = hasPermission('offerte:export');
  const [templateId, setTemplateId] = useState(offer.template_id || '');
  const [values, setValues] = useState(offer.section_values || {});
  const [preview, setPreview] = useState('');
  const [generated, setGenerated] = useState<Generated | null>(null);
  const config = offer.document_config;
  const templateChanged = templateId !== (offer.template_id || '');
  const textChanged = JSON.stringify(values) !== JSON.stringify(offer.section_values || {});
  const dirty = templateChanged || textChanged;
  useEffect(() => {
    setTemplateId(offer.template_id || '');
    setValues(offer.section_values || {});
    setPreview('');
    setGenerated(null);
  }, [offer.id, offer.updated_at]);
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['offer', offer.id] }),
      queryClient.invalidateQueries({ queryKey: ['offer-revisions', offer.id] }),
      queryClient.invalidateQueries({ queryKey: ['revision-documents', offer.id] }),
      queryClient.invalidateQueries({ queryKey: ['offers'] }),
    ]);
  };
  const save = useMutation({
    mutationFn: () => offersAPI.updateOffer(offer.id, templateChanged ? { template_id: templateId } : { section_values: values }),
    onSuccess: refresh,
  });
  const showPreview = useMutation({
    mutationFn: async () => (await apiClient.client.get<string>(`/offerte/${offer.id}/html-preview`)).data,
    onSuccess: html => setPreview(DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style'] })),
  });
  const generate = useMutation({
    mutationFn: async () => (await revisionsAPI.exportCurrent(offer.id)).data as Generated,
    onSuccess: async data => { await refresh(); setGenerated(data); },
  });
  const busy = save.isPending || showPreview.isPending || generate.isPending;
  useEffect(() => { onDirtyChange(dirty || busy); }, [dirty, busy, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const unavailable = blocked || busy;
  const selectable = templates.filter(t => t.tipo === 'offerta');
  return <section aria-label="Contenuto documento" className="flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white">
    <div className="flex flex-wrap items-end gap-3 border-b p-4">
      <div className="min-w-64 flex-1">
        <label htmlFor="document-template" className="mb-1 block text-sm font-medium">Template del documento</label>
        <select id="document-template" value={templateId} disabled={!canEdit || unavailable || textChanged}
          onChange={e => setTemplateId(e.target.value)} className="w-full rounded border border-gray-300 p-2">
          <option value="" disabled={!!config}>Scegli un template</option>
          {selectable.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      </div>
      {canEdit && <button type="button" disabled={unavailable || !templateId || !templateChanged} onClick={() => save.mutate()} className="rounded border px-3 py-2 disabled:opacity-50">Applica template</button>}
      <a href="/templates" target="_blank" rel="noreferrer" className="py-2 text-sm text-primary-700 underline">Gestisci template</a>
    </div>
    <div className="flex flex-wrap items-center gap-3 border-b bg-gray-50 p-4">
      {canEdit && <button type="button" disabled={unavailable || !textChanged || templateChanged} onClick={() => save.mutate()} className="rounded border bg-white px-3 py-2 disabled:opacity-50">Salva sezioni</button>}
      <button type="button" disabled={unavailable || dirty} onClick={() => showPreview.mutate()} className="rounded border bg-white px-3 py-2 disabled:opacity-50">Anteprima documento</button>
      {canExport && <button type="button" disabled={unavailable || dirty || !config} onClick={() => generate.mutate()} className="rounded bg-primary-600 px-3 py-2 text-white disabled:opacity-50">{generate.isPending ? 'Generazione in corso…' : 'Genera DOCX e PDF'}</button>}
      <span className="text-sm text-gray-600">{config ? `Template attivo: ${config.nome}` : 'Il contenuto precedente è conservato. Applica un template per compilare le sezioni.'}</span>
    </div>
    {blocked && <p role="status" className="px-4 pt-3 text-sm text-amber-800">Salva prima le modifiche in Dettagli offerta.</p>}
    {dirty && <p role="status" className="px-4 pt-3 text-sm text-amber-800">{templateChanged ? 'Applica il template per caricare le sue sezioni. I testi già salvati restano conservati.' : 'Salva le sezioni prima di visualizzare o generare il documento.'}</p>}
    {!canEdit && <p className="px-4 pt-3 text-sm text-gray-600">Documento emesso: per modificare il testo, crea una nuova revisione qui sotto.</p>}
    {(save.isError || showPreview.isError || generate.isError) && <p role="alert" className="p-4 text-red-700">{message(save.error || showPreview.error || generate.error)}</p>}
    {generated && <div role="status" className="border-b p-4"><p className="mb-2 font-medium">{generated.bozza ? 'Bozza' : 'Documento emesso'} · Revisione {generated.numero_revisione} · {offer.numero}</p><div className="flex gap-4">{generated.documents.filter(d => ['pdf', 'docx'].includes(d.tipo)).map(d => <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="text-primary-700 underline">Scarica {d.tipo.toUpperCase()}</a>)}</div></div>}
    <div className="max-h-[55dvh] overflow-auto overscroll-contain">
      {config && <OfferSections sections={config.sezioni || []} values={values} onChange={setValues} readOnly={!canEdit || unavailable || templateChanged} />}
      {preview && <div className="border-t p-4"><h3 className="mb-2 font-medium">Anteprima del documento salvato</h3><iframe title="Anteprima documento" sandbox="" srcDoc={preview} className="h-[55dvh] w-full rounded border bg-white" /></div>}
    </div>
    <div className="border-t p-4"><OfferRevisions id={offer.id} canRevise={offer.stato === 'inviata' && hasPermission('offerte:edit') && !blocked && !dirty && !busy} /></div>
  </section>;
}
